import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import {
  AccountInfo,
  AuthenticationResult,
  BrowserAuthError,
  EventMessage,
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
} from '@azure/msal-browser';
import { filter, Subscription, BehaviorSubject } from 'rxjs';
import { environment } from '../environments/environment';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private lastRefreshTime: Date | null = null;
  private readonly refreshTokenIntervalInMinutes = 1;
  private readonly lastRefreshTimeKey = 'lastRefreshTime';
  private msalSubjectSubscription: Subscription = Subscription.EMPTY;
  private inProgressSubscription: Subscription = Subscription.EMPTY;
  private currentlyhandlingError = false;
  private currentlyhandlingErrorResponse: Promise<AuthenticationResult | null> =
    Promise.resolve(null);

  private readonly loaderSubject = new BehaviorSubject<boolean>(false);
  public loader$ = this.loaderSubject.asObservable();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router,
    private userService: UserService
  ) {
    this.loadLastRefreshTime();

    this.msalBroadcastService.inProgress$
      .pipe(filter((status: InteractionStatus) => status === InteractionStatus.None))
      .subscribe(() => {
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts?.length > 0) {
          this.renewTokenSilently();
        }
      });
  }

  async renewTokenSilently(): Promise<void> {
    const authResult = await this.acquireTokenSilently();
    this.msalService.instance.setActiveAccount(
      authResult ? authResult.account : null
    );
  }

  loadLastRefreshTime(): void {
    const storedTime = localStorage.getItem(this.lastRefreshTimeKey);
    this.lastRefreshTime = storedTime ? new Date(parseInt(storedTime)) : null;
  }

  saveLastRefreshTime(time: Date): void {
    this.lastRefreshTime = time;
    localStorage.setItem(this.lastRefreshTimeKey, time.getTime().toString());
  }

  clearStoredRefreshTime() {
    localStorage.removeItem(this.lastRefreshTimeKey);
    this.lastRefreshTime = null;
  }

  shouldRefreshToken(): boolean {
    if (!this.lastRefreshTime) {
      return true;
    }
    const timeDifference =
      new Date().getTime() - this.lastRefreshTime.getTime();
    const minutesPassed = Math.floor(timeDifference / 60000);
    return minutesPassed > this.refreshTokenIntervalInMinutes;
  }

  async acquireTokenSilently(): Promise<AuthenticationResult | null> {
    const account = this.msalService.instance.getAllAccounts()[0];
    const forceRefresh = this.shouldRefreshToken();
    forceRefresh && this.saveLastRefreshTime(new Date());
    if (account && !this.currentlyhandlingError) {
      try {
        return await this.msalService.instance.acquireTokenSilent({
          account,
          scopes: environment.apiConfig.scopes,
          forceRefresh: forceRefresh,
        });
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          try {
            if (!this.currentlyhandlingError) {
              this.currentlyhandlingError = true;
              this.currentlyhandlingErrorResponse =
                this.acquireTokenWithPopup();
            }
          } catch (error) {
            this.clearStoredRefreshTime();
            this.logout();
          }
        } else {
          this.clearStoredRefreshTime();
          this.logout();
        }
      }
    }
    const value = await this.currentlyhandlingErrorResponse;
    this.currentlyhandlingError = false;
    return value;
  }

  async acquireTokenWithPopup(): Promise<AuthenticationResult | null> {
    const account = this.msalService.instance.getAllAccounts()[0];
    try {
      const response = await this.msalService.instance.acquireTokenPopup({
        account,
        scopes: environment.apiConfig.scopes,
      });
      return Promise.resolve(response);
    } catch (error) {
      if (
        error instanceof BrowserAuthError &&
        error.errorCode === 'popup_window_error'
      ) {
        alert('Popup blocked. Please enable popups and reload.');
        return Promise.resolve(null);
      } else {
        throw error;
      }
    }
  }

  isAuthenticated(): boolean {
    return this.msalService.instance.getAllAccounts().length > 0;
  }

  login(): void {
    this.msalService.loginRedirect({ 
      scopes: environment.apiConfig.scopes,
      redirectUri: environment.msalConfig.auth.redirect_uri
    });
  }

  logout(): void {
    this.clearStoredRefreshTime();
    this.msalService.logout();
  }

  checkAccount(): void {
    this.msalSubjectSubscription = this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        const payload = result.payload as AuthenticationResult;
        this.msalService.instance.setActiveAccount(payload.account);

        if (payload.idToken) {
          localStorage.setItem('idToken', payload.idToken);
        }
      });

    this.inProgressSubscription = this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None)
      )
      .subscribe(() => {
        try {
          this.navigateIfAuthenticated();
        } catch (error) {
          console.error('Error:', error);
        }
      });
  }

  getMsalService(): MsalService {
    return this.msalService;
  }

  navigateIfAuthenticated(): void {
    const isAuthenticated = this.msalService.instance.getAllAccounts().length > 0;
    
    if (isAuthenticated) {
      this.loaderSubject.next(true);
      
      // Verify user with backend API (currently mocked)
      this.userService.verifyUser(this.userEmail).then((response) => {
        this.loaderSubject.next(false);
        
        if (response?.user_exists) {
          // Navigate to home page
          this.router.navigate(['/home']);
        } else {
          alert('You do not have access to this application. Please contact your administrator.');
          this.logout();
        }
      }).catch((error) => {
        this.loaderSubject.next(false);
        alert('Unable to verify user access. Please try again or contact your administrator.');
        this.logout();
      });
    } else {
      this.loaderSubject.next(false);
      this.router.navigate(['/login']);
    }
  }

  ngOnDestroy(): void {
    this.msalSubjectSubscription.unsubscribe();
    this.inProgressSubscription.unsubscribe();
  }

  get activeAccount(): AccountInfo | null {
    return this.msalService?.instance?.getActiveAccount();
  }

  get userEmail(): string {
    return this.activeAccount?.username || '';
  }

  get userName(): string {
    return this.activeAccount?.name || '';
  }

  get allAccounts(): AccountInfo[] {
    return this.msalService.instance.getAllAccounts();
  }
}
